import * as React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { useLineAttention } from "@/features/attention/hooks";
import { SuppressedAlarmsNote } from "@/features/attention/SuppressedAlarmsNote";
import { JustifyEventDialog } from "@/features/machine-sidebar/JustifyEventDialog";
import { AttentionRow } from "./AttentionRow";
import { AttentionDetailDialog } from "./AttentionDetailDialog";

export interface AttentionCardProps {
  lineId: string;
}

const MAX_VISIBLE = 8;

export function AttentionCard({ lineId }: AttentionCardProps): React.ReactElement {
  const attentionQuery = useLineAttention(lineId);
  const { data, isLoading } = attentionQuery;
  const [justifyId, setJustifyId] = React.useState<string | null>(null);
  const [detailId, setDetailId] = React.useState<string | null>(null);

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="bg-bg-muted h-24 rounded motion-safe:animate-pulse-dot" />
      </Card>
    );
  }

  if (attentionQuery.isError) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.QueryError
          error={attentionQuery.error}
          onRetry={() => void attentionQuery.refetch()}
        />
      </Card>
    );
  }

  const items = data?.items ?? [];
  const pending = data?.summary.pendingJustifications ?? 0;
  const suppressed = data?.summary.suppressedAlarms ?? 0;

  if (items.length === 0) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.Empty title="Tudo normal" description="Nada exige atenção nesta linha." />
      </Card>
    );
  }

  const visible = items.slice(0, MAX_VISIBLE);
  const overflow = items.length - visible.length;
  const detailOcc = detailId ? (items.find((o) => o.occurrenceId === detailId) ?? null) : null;

  return (
    <>
      <Card className="space-y-3 rounded-2xl px-[22px] py-5" aria-labelledby="attention-card-h2">
        <header className="flex items-center justify-between gap-2">
          <h2
            id="attention-card-h2"
            className="text-[14px] font-semibold tracking-tight text-text-primary"
          >
            Atenção da linha
          </h2>
          <span className="font-mono text-[11px] text-text-tertiary">
            {items.length} item{items.length === 1 ? "" : "s"}
            {pending > 0 ? ` · ${pending} a justificar` : ""}
          </span>
        </header>

        <ul className="space-y-2">
          {visible.map((occ) => (
            <AttentionRow
              key={occ.occurrenceId}
              occ={occ}
              onJustify={setJustifyId}
              onOpenDetails={setDetailId}
            />
          ))}
        </ul>

        <SuppressedAlarmsNote count={suppressed} />

        {overflow > 0 ? (
          <Link
            to={`/alarms?lineId=${lineId}`}
            className="block rounded text-[12px] text-brand-700 hover:underline focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
          >
            + {overflow} item{overflow === 1 ? "" : "s"} — ver tudo
          </Link>
        ) : null}
      </Card>

      {/* Detalhes completos da ocorrência via modal acionado pela linha */}
      <AttentionDetailDialog
        occ={detailOcc}
        open={detailOcc != null}
        onClose={() => setDetailId(null)}
        onJustify={setJustifyId}
      />

      {/* Justificar acessível a partir da visão de atenção, sem run ativa */}
      {justifyId ? (
        <JustifyEventDialog eventId={justifyId} open onClose={() => setJustifyId(null)} />
      ) : null}
    </>
  );
}
